// Options Flow page shell — chrome header + a stacked layout. The page reads the
// active envelope + venue/token/window selection from OptionsFlowContext,
// resolves the header meta, and asks the dispatch for its layout sections.
// Placement is by VIZ (so a new report of a known viz still appears), with
// net_flow split into its top KPI tiles and its lower charts. Top-to-bottom:
//   selector row (window/token/venue) -> cross-venue comparison (collapsible)
//     -> KPI tiles
//     -> composite region: [top_flow ranked | Volume-by-session + Block-vs-Screen stack]
//     -> [data tables: flow_by_expiry, then chain]
//     -> [time charts: net-premium cumulative + daily, then timeseries family]
//     -> Flow composition (full-width, pinned to the very bottom)

import { useContext } from 'react';
import { Box } from '@mui/material';

import { OptionsFlowContext } from '../../providers/options-flow/options-flow-provider';
import {
  fmtAsOfTime,
  resolveHeaderMeta,
  resolveWindowMeta,
  structuresNotTaggedMessage,
  venueLabel,
} from '../../providers/options-flow/options-flow.helpers';
import { tokens } from '../../styles/tokens';
// Reused live market-context band (BTC/ETH index · 24h · funding · implied vol) —
// sources the app market WS directly (WebSocketContext), independent of the flow
// feed. `showVols` appends the ATMF/25RR (1W/1M) tiles per row.
import { BtcTickerBand } from '../portfolio-performance/components/market-ticker-band';
import { FlowHeader } from './components/flow-header';
import { FlowEmptyState } from './components/flow-empty-state';
import { WindowSelector } from './components/window-selector';
import { TokenSelector } from './components/token-selector';
import { VenueSelector } from './components/venue-selector';
import { LiveEdgeHint, LowLiquidityTag } from './components/token-status';
import { VenueComparisonPanel } from './components/venue-comparison-panel';
import { getLayoutSections } from './dispatch';

const OptionsFlowPage = () => {
  const {
    payload,
    status,
    activeWindow,
    setActiveWindow,
    availableWindows,
    windowCoverage,
    venue,
    setVenue,
    venues,
    activeVenue,
    token,
    setToken,
    tokens: tokenList,
    venueComparison,
    refresh,
    lastUpdated,
  } = useContext(OptionsFlowContext);

  const headerMeta = resolveHeaderMeta(payload);
  const windowMeta = resolveWindowMeta(payload);
  // "as of HH:MM UTC" freshness stamp from the snapshot's generated_utc.
  const asOfTime = fmtAsOfTime(headerMeta?.generated_utc ?? windowMeta?.generated_utc);
  // Wall-clock HH:MM (UTC) the FE last pulled a snapshot — updates on the Refresh
  // button + the hourly auto-poll, so a click gives visible feedback even when
  // generated_utc (the backend build stamp) hasn't advanced.
  const lastRefreshed =
    lastUpdated != null ? new Date(lastUpdated).toISOString().slice(11, 16) : null;
  // Venue degradation: on a venue that doesn't tag structures, the feed omits the
  // structures report. Rather than a standalone stand-in card, the explanation is
  // rendered IN-PLACE as the 4th slot of Flow composition (IM-1945) — so we pass
  // the venue-named message down to the layout for that slot.
  const structuresEmptyMessage =
    activeVenue?.structures_supported === false
      ? structuresNotTaggedMessage(venueLabel(activeVenue))
      : undefined;
  const { top, composite, tables, charts, bottom } = getLayoutSections(payload, {
    structuresEmptyMessage,
  });
  const sideStack = composite.sideStack;
  const hasComposite = composite.ranked !== null || sideStack.length > 0;
  const isEmpty =
    top.length === 0 &&
    !hasComposite &&
    tables.length === 0 &&
    charts.length === 0 &&
    bottom.length === 0;

  return (
    <Box sx={{ backgroundColor: tokens.bg.page, minHeight: '100%', pt: 8 }}>
      {/* Page-level zoom: William prefers the ~125% feel for the chain/tables'
          readability, but true 125% browser zoom wraps the global appbar. Scoping
          real `zoom` (Chrome; crisp, reflows to fit — no page h-scroll) to just
          this content container scales tiles/tables/chain/donuts uniformly while
          the appbar (rendered outside this page) stays single-line. */}
      <Box sx={{ zoom: 1.2, px: '26px', pt: '22px', pb: '30px' }}>
        {/* Live market-context band at the very top (independent of the flow feed).
            Distinct channel prefix so it doesn't collide with PP's band — the app
            mounts every route's tabs at once, so both are live simultaneously. */}
        {/* One market-context band: BTC + ETH rows, each with index/change/
            high/low/funding (live) + ATMF 1W/1M + 25RR 1W/1M (30s poll). Always
            both tokens, independent of the selected flow token. */}
        <Box sx={{ mb: 2 }}>
          <BtcTickerBand subscriptionIdPrefix="CANDLE_SUBSCRIPTION_OF_TICKER" showVols />
        </Box>

        <FlowHeader headerMeta={headerMeta} windowMeta={windowMeta} />

        {/* Controls band — full width: WINDOW on the left (+ its T-1 / "as of
            yesterday" freshness hint), then TOKENS and VENUE on the right with
            VENUE ANCHORED to the far-right edge. Order is [token][venue] so the
            variable-width token block (6 chips on Deribit, 2 on OKX) expands/
            contracts to the LEFT of the fixed venue selector — the venue chips
            never jump when you switch venue. All three switch CLIENT-SIDE off
            the one nested bundle. Below md the two clusters wrap. */}
        <Box
          sx={{
            mt: 1.5,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            rowGap: 1.25,
            columnGap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
            <WindowSelector
              value={activeWindow}
              available={availableWindows}
              coverage={windowCoverage}
              onChange={setActiveWindow}
            />
            <LiveEdgeHint />
            {/* Snapshot freshness — HH:MM UTC from the payload's
                `meta.generated_utc` (flow_claude's authoritative build stamp),
                so the 5-min refresh cadence is legible. Omitted when absent. */}
            {asOfTime && (
              <Box
                sx={{
                  fontSize: 10.5,
                  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                  color: tokens.text.muted,
                }}
              >
                as of {asOfTime}
              </Box>
            )}
            {/* Manual refresh — auto-poll is now hourly (AWS-egress mitigation),
                so users can force an immediate re-fetch; the stamp beside it is
                the wall-clock of the last pull, giving a click visible feedback. */}
            <Box
              component="button"
              type="button"
              onClick={refresh}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.75,
                py: 0.25,
                fontSize: 10.5,
                fontFamily: 'inherit',
                color: tokens.text.muted,
                background: 'transparent',
                border: `1px solid ${tokens.border.default}`,
                borderRadius: 1,
                cursor: 'pointer',
                '&:hover': { borderColor: tokens.text.muted, color: tokens.text.primary },
              }}
            >
              ↻ Refresh
            </Box>
            {lastRefreshed && (
              <Box
                sx={{
                  fontSize: 10.5,
                  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                  color: tokens.text.muted,
                }}
              >
                refreshed {lastRefreshed}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
            <LowLiquidityTag />
            <TokenSelector value={token} tokenList={tokenList} onChange={setToken} />
            {/* Last child of the right-anchored (space-between) cluster, so it
                sits at the container's far-right edge and stays put while the
                token block to its left changes width across venues. */}
            <VenueSelector value={venue} venues={venues} onChange={setVenue} />
          </Box>
        </Box>

        {/* Cross-venue market overview (v2) — collapsible, spans ALL venues,
            independent of the venue selector. Sits directly BELOW the selector
            row and ABOVE the IMMERSIVE flow content (client feedback IM-1934).
            Guarded so single-venue feeds (no venue_comparison) leave no gap —
            the panel itself also self-hides when it has nothing to render. */}
        {venueComparison && (
          <Box sx={{ mt: 2 }}>
            <VenueComparisonPanel report={venueComparison} />
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          {status === 'loading' && <FlowEmptyState message="Loading flow…" height={200} />}
          {status === 'error' && (
            <FlowEmptyState message="Could not load the flow feed" height={200} />
          )}
          {status === 'ready' && isEmpty && (
            <FlowEmptyState message="No reports in this payload" height={200} />
          )}

          {status === 'ready' && !isEmpty && (
            <>
              {/* Top: KPI tiles only (Flow composition is pinned to the bottom). */}
              {top.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', mb: '14px' }}>
                  {top}
                </Box>
              )}

              {/* Composite region: LEFT the tall Top-instruments ranked card,
                  RIGHT the single-donut breakdowns (Volume by session above
                  Block vs Screen) stacked. Below md it collapses to one column:
                  ranked, then session, then block-vs-screen. */}
              {hasComposite && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 3fr) minmax(0, 2fr)' },
                    gap: '14px',
                    alignItems: 'start',
                    mb: '14px',
                  }}
                >
                  {composite.ranked && <Box>{composite.ranked}</Box>}
                  {sideStack.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      {sideStack}
                    </Box>
                  )}
                </Box>
              )}

              {/* Data tables — responsive 2-col grid; full-span cards stretch. */}
              {tables.length > 0 && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: '14px',
                    alignItems: 'start',
                    mb: '14px',
                  }}
                >
                  {tables.map((card) => (
                    <Box
                      key={card.name}
                      sx={{ gridColumn: card.span === 'full' ? '1 / -1' : 'auto' }}
                    >
                      {card.element}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Time charts — net_flow cumulative + daily, then timeseries. */}
              {charts.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>{charts}</Box>
              )}

              {/* Flow composition — pinned to the VERY BOTTOM (William, live):
                  the 4-donut panel (Call vs Put / Taker buy vs sell / Positioning
                  / Structures) sits below every other section. */}
              {bottom.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', mt: '14px' }}>
                  {bottom}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OptionsFlowPage;
